import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { z } from 'zod';
import {
  adsResponseSchema,
  attentionResponseSchema,
  callPatternsResponseSchema,
  callSummaryResponseSchema,
  sellerCallsResponseSchema,
  callHighlightsResponseSchema,
  authResponseSchema,
  twoFactorEnableResponseSchema,
  twoFactorSetupResponseSchema,
  clientsResponseSchema,
  dataQualityResponseSchema,
  filtersResponseSchema,
  funnelResponseSchema,
  logoutResponseSchema,
  lossesResponseSchema,
  marketingResponseSchema,
  marketingTrendResponseSchema,
  overviewResponseSchema,
  salespersonDetailResponseSchema,
  systemStatusResponseSchema,
  teamResponseSchema,
  trendResponseSchema,
  type UserDto,
} from '@crm/shared/contracts';
import type { BusinessDate } from '@crm/shared/time';
import { ApiError, apiRequest, type QueryParams } from './api.ts';
import { useToday } from './clock.tsx';
import { includesToday } from './periods.ts';
import { shouldRetry } from './query-client.ts';
import type { DashboardParams } from './url-state.ts';

/** Response types come straight from the shared contracts: server and client cannot drift apart. */
export type OverviewResponse = z.output<typeof overviewResponseSchema>;
export type TrendResponse = z.output<typeof trendResponseSchema>;
export type FunnelResponse = z.output<typeof funnelResponseSchema>;
export type LossesResponse = z.output<typeof lossesResponseSchema>;
export type TeamResponse = z.output<typeof teamResponseSchema>;
export type SalespersonResponse = z.output<typeof salespersonDetailResponseSchema>;
export type MarketingResponse = z.output<typeof marketingResponseSchema>;
export type MarketingTrendResponse = z.output<typeof marketingTrendResponseSchema>;
export type AttentionResponse = z.output<typeof attentionResponseSchema>;
export type DataQualityResponse = z.output<typeof dataQualityResponseSchema>;
export type SystemStatusResponse = z.output<typeof systemStatusResponseSchema>;
export type FiltersResponse = z.output<typeof filtersResponseSchema>;
export type SellerCallsResponse = z.output<typeof sellerCallsResponseSchema>;
export type CallHighlightsResponse = z.output<typeof callHighlightsResponseSchema>;
export type CallSummaryResponse = z.output<typeof callSummaryResponseSchema>;
export type CallPatternsResponse = z.output<typeof callPatternsResponseSchema>;
export type ClientsResponse = z.output<typeof clientsResponseSchema>;
export type AdsResponse = z.output<typeof adsResponseSchema>;
export type AdLevel = AdsResponse['data']['level'];

const FIVE_MINUTES = 5 * 60_000;

/** The query string of a period endpoint. `compare` is only sent where the API accepts it. */
export function periodParams(
  params: DashboardParams,
  options: { compare?: boolean; filters?: boolean } = { compare: true, filters: true },
): QueryParams {
  return {
    from: params.from,
    to: params.to,
    ...(options.compare ? { compare: params.compare } : {}),
    ...(options.filters && params.salespersonId.length
      ? { salespersonId: params.salespersonId }
      : {}),
    ...(options.filters && params.sourceId.length ? { sourceId: params.sourceId } : {}),
  };
}

/**
 * A period query. Periods that reach today refetch on focus and every 5 minutes; past periods are
 * cached longer (docs/DASHBOARD_SPECIFICATION.md §5). While a new period loads, the previous
 * figures stay on screen.
 */
function usePeriodQuery<S extends z.ZodType>(
  name: string,
  path: string,
  schema: S,
  params: QueryParams,
  periodTo: string,
  enabled = true,
): UseQueryResult<z.output<S>, ApiError> {
  const today = useToday();
  const live = includesToday(periodTo as BusinessDate, today);
  return useQuery<z.output<S>, ApiError>({
    queryKey: [name, params],
    queryFn: ({ signal }) => apiRequest(path, { params, schema, signal }) as Promise<z.output<S>>,
    placeholderData: keepPreviousData,
    staleTime: live ? 60_000 : 10 * 60_000,
    refetchInterval: live ? FIVE_MINUTES : false,
    refetchOnWindowFocus: live,
    retry: shouldRetry,
    enabled,
  });
}

export const useOverview = (p: DashboardParams) =>
  usePeriodQuery('overview', '/overview', overviewResponseSchema, periodParams(p), p.to);

export const useTrend = (p: DashboardParams, metrics: readonly string[]) =>
  usePeriodQuery(
    'trend',
    '/overview/trend',
    trendResponseSchema,
    { ...periodParams(p, { filters: true }), metrics: metrics.join(',') },
    p.to,
  );

export const useFunnel = (p: DashboardParams, mode: 'cohort' | 'activity') =>
  usePeriodQuery(
    'funnel',
    '/funnel',
    funnelResponseSchema,
    { ...periodParams(p, { filters: true }), mode },
    p.to,
  );

export const useLosses = (p: DashboardParams, basis: 'lost_at' | 'cohort') =>
  usePeriodQuery(
    'losses',
    '/funnel/losses',
    lossesResponseSchema,
    { ...periodParams(p, { filters: true }), basis },
    p.to,
  );

export const useTeam = (p: DashboardParams, enabled = true) =>
  usePeriodQuery('team', '/team/performance', teamResponseSchema, periodParams(p), p.to, enabled);

export const useSalesperson = (id: string, p: DashboardParams) =>
  usePeriodQuery(
    // The person is in the path (and the cache key); the API rejects unknown query parameters.
    `salesperson:${id}`,
    `/team/salespeople/${id}`,
    salespersonDetailResponseSchema,
    periodParams(p, { filters: false }),
    p.to,
  );

export const useMarketing = (p: DashboardParams, enabled = true) =>
  usePeriodQuery(
    'marketing',
    '/marketing/sources',
    marketingResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
    enabled,
  );

export const useMarketingTrend = (p: DashboardParams, enabled = true) =>
  usePeriodQuery(
    'marketing-trend',
    '/marketing/trend',
    marketingTrendResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
    enabled,
  );

export const useCallSummary = (p: DashboardParams) =>
  usePeriodQuery(
    'call-summary',
    '/calls/summary',
    callSummaryResponseSchema,
    periodParams(p),
    p.to,
  );

export const useSellerCalls = (p: DashboardParams) =>
  usePeriodQuery(
    'seller-calls',
    '/calls/by-salesperson',
    sellerCallsResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
  );

export const useCallHighlights = (p: DashboardParams) =>
  usePeriodQuery(
    'call-highlights',
    '/calls/highlights',
    callHighlightsResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
  );

export const useCallPatterns = (p: DashboardParams) =>
  usePeriodQuery(
    'call-patterns',
    '/calls/patterns',
    callPatternsResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
  );

export const useClients = (
  p: DashboardParams,
  page: { limit: number; offset: number; search: string },
) =>
  usePeriodQuery(
    'clients',
    '/clients',
    clientsResponseSchema,
    {
      ...periodParams(p, { filters: true }),
      limit: String(page.limit),
      offset: String(page.offset),
      ...(page.search ? { search: page.search } : {}),
    },
    p.to,
  );

export const useAds = (p: DashboardParams, level: AdLevel, enabled = true) =>
  usePeriodQuery(
    'ads',
    '/marketing/ads',
    adsResponseSchema,
    { ...periodParams(p), level },
    p.to,
    enabled,
  );

export const useDataQuality = (p: DashboardParams) =>
  usePeriodQuery(
    'data-quality',
    '/data-quality',
    dataQualityResponseSchema,
    periodParams(p, { filters: true }),
    p.to,
  );

/** State endpoints have no period; they refresh on focus and every 5 minutes. */
function useSnapshotQuery<S extends z.ZodType>(
  name: string,
  path: string,
  schema: S,
): UseQueryResult<z.output<S>, ApiError> {
  return useQuery<z.output<S>, ApiError>({
    queryKey: [name],
    queryFn: ({ signal }) => apiRequest(path, { schema, signal }) as Promise<z.output<S>>,
    staleTime: 60_000,
    refetchInterval: FIVE_MINUTES,
    refetchOnWindowFocus: true,
    retry: shouldRetry,
  });
}

export const useAttention = () =>
  useSnapshotQuery('attention', '/attention', attentionResponseSchema);
export const useSystemStatus = () =>
  useSnapshotQuery('system-status', '/system/status', systemStatusResponseSchema);
export const useFilters = () => useSnapshotQuery('filters', '/meta/filters', filtersResponseSchema);

// ---- session ---------------------------------------------------------------------------------

export const ME_KEY = ['me'] as const;

/** The signed-in user, or `null` when there is no valid session (a 401 is an answer, not an error). */
export function useMe(): UseQueryResult<UserDto | null, ApiError> {
  return useQuery<UserDto | null, ApiError>({
    queryKey: ME_KEY,
    queryFn: async ({ signal }) => {
      try {
        const response = await apiRequest('/auth/me', { schema: authResponseSchema, signal });
        return response.data.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<UserDto, ApiError, { email: string; password: string; otp?: string }>({
    mutationFn: async (credentials) => {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: credentials,
        schema: authResponseSchema,
      });
      return response.data.user;
    },
    onSuccess: (user) => {
      // Figures fetched before this login belonged to nobody: start clean.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== ME_KEY[0] });
      queryClient.setQueryData(ME_KEY, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError>({
    mutationFn: async () => {
      await apiRequest('/auth/logout', { method: 'POST', body: {}, schema: logoutResponseSchema });
    },
    onSettled: () => {
      // Mark the session as ended first: the guard sees `null` and sends the user to the login
      // page at once. Clearing the cache first would detach that observer and leave the page as it was.
      queryClient.setQueryData(ME_KEY, null);
      queryClient.removeQueries({
        predicate: (query) => JSON.stringify(query.queryKey) !== JSON.stringify(ME_KEY),
      });
    },
  });
}

export function useTwoFactorSetup() {
  return useMutation<{ secret: string; otpauthUri: string }, ApiError>({
    mutationFn: async () =>
      (
        await apiRequest('/auth/2fa/setup', {
          method: 'POST',
          body: {},
          schema: twoFactorSetupResponseSchema,
        })
      ).data,
  });
}

export function useTwoFactorEnable() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (code) => {
      await apiRequest('/auth/2fa/enable', {
        method: 'POST',
        body: { code },
        schema: twoFactorEnableResponseSchema,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });
}
